import {
  IConformationBreedResult,
  IConformationClassResult,
  IConformationGroupResult,
  IConformationNonLicensedBreedResult,
  IConformationRegularBreedAwards,
  IConformationShowAwards,
  IConformationVarietyAwards,
  IJuniorShowmanshipResults,
  IShow,
} from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { licensedShowElement, classSectionKey } from "./licensed";
import { conformationOf } from "./stored";
import { ConformationStream, IClassSection, LicensedStream } from "./types";

/** Blank picker value → omitted from the payload. */
export function cleanAward(value?: string): string | undefined {
  return value || undefined;
}

/** PUT conformation/breed-results body — Regular or Altered. */
export function buildBreedPayload(input: {
  stream: LicensedStream;
  breed: string;
  breedGroup: string;
  sections: IClassSection[];
  placementsBySection: Record<string, string[]>;
  awards: IConformationRegularBreedAwards;
}): IConformationBreedResult {
  const showElement = licensedShowElement(input.stream);
  const classResults: IConformationClassResult[] = input.sections.map((section) => ({
    show_element: showElement,
    level: section.level,
    sex: section.sex,
    ...(section.variety ? { variety: section.variety } : {}),
    placements: (input.placementsBySection[classSectionKey(section)] || []).filter(Boolean),
  }));

  const varietyAwards: IConformationVarietyAwards[] = (input.awards.variety_awards || [])
    .map((row) => ({
      variety: row.variety,
      best_male: cleanAward(row.best_male),
      reserve_best_male: cleanAward(row.reserve_best_male),
      best_female: cleanAward(row.best_female),
      reserve_best_female: cleanAward(row.reserve_best_female),
    }))
    .filter(
      (row) => row.best_male || row.reserve_best_male || row.best_female || row.reserve_best_female
    );

  return {
    breed: input.breed,
    breed_group: input.breedGroup,
    stream: input.stream,
    class_results: classResults,
    awards: {
      best_of_breed: cleanAward(input.awards.best_of_breed),
      grand_champion: cleanAward(input.awards.grand_champion),
      reserve_grand_champion: cleanAward(input.awards.reserve_grand_champion),
      champion: cleanAward(input.awards.champion),
      reserve_champion: cleanAward(input.awards.reserve_champion),
      best_of_winners: cleanAward(input.awards.best_of_winners),
      best_male: cleanAward(input.awards.best_male),
      reserve_best_male: cleanAward(input.awards.reserve_best_male),
      best_female: cleanAward(input.awards.best_female),
      reserve_best_female: cleanAward(input.awards.reserve_best_female),
      ...(varietyAwards.length ? { variety_awards: varietyAwards } : {}),
    },
  };
}

/** PUT conformation/non-licensed-breed-results body — Novice Puppy or Veteran. */
export function buildNonLicensedPayload(input: {
  level: "novice_puppy" | "veteran";
  breed: string;
  breedGroup: string;
  male: string[];
  female: string[];
  bestOfBreed: string;
}): IConformationNonLicensedBreedResult {
  return {
    breed: input.breed,
    breed_group: input.breedGroup,
    level: input.level,
    male_placements: input.male.filter(Boolean),
    female_placements: input.female.filter(Boolean),
    best_of_breed: cleanAward(input.bestOfBreed),
  };
}

/** PUT conformation/non-licensed-breed-results body — Brace. */
export function buildBracePayload(input: {
  breed: string;
  breedGroup: string;
  placements: string[];
  bestBrace: string;
}): IConformationNonLicensedBreedResult {
  return {
    breed: input.breed,
    breed_group: input.breedGroup,
    level: "brace",
    placements: input.placements.filter(Boolean),
    best_of_breed: cleanAward(input.bestBrace),
  };
}

/** PUT conformation/group-results body — Regular only. */
export function buildGroupPayload(input: {
  breedGroup: string;
  placements: string[];
}): IConformationGroupResult {
  return {
    breed_group: input.breedGroup,
    stream: "regular",
    placements: input.placements.filter(Boolean),
  };
}

export type ShowAwardsForm = Record<
  ConformationStream,
  { best_in_show: string; reserve_best_in_show: string }
>;

/**
 * PUT conformation/show-results body. Carries the existing object forward, writes the
 * per-stream pairs, and mirrors Regular BIS onto the legacy flat `best_in_show`.
 */
export function buildShowAwardsPayload(show: IShow, form: ShowAwardsForm): IConformationShowAwards {
  const existing = conformationOf(show)?.show_results || {};
  return {
    ...existing,
    regular: {
      best_in_show: form.regular.best_in_show || "",
      reserve_best_in_show: form.regular.reserve_best_in_show || "",
    },
    altered: {
      best_in_show: form.altered.best_in_show || "",
      reserve_best_in_show: form.altered.reserve_best_in_show || "",
    },
    novice_puppy: {
      best_in_show: form.novice_puppy.best_in_show || "",
      reserve_best_in_show: form.novice_puppy.reserve_best_in_show || "",
    },
    veteran: {
      best_in_show: form.veteran.best_in_show || "",
      reserve_best_in_show: form.veteran.reserve_best_in_show || "",
    },
    brace: {
      best_in_show: form.brace.best_in_show || "",
    },
    best_in_show: form.regular.best_in_show || "",
  };
}

/** PUT conformation/junior-results body. */
export function buildJuniorPayload(input: {
  classes: string[];
  placementsByLevel: Record<string, string[]>;
  bestJunior: string;
}): IJuniorShowmanshipResults {
  const classResults: IConformationClassResult[] = input.classes.map((level) => ({
    show_element: Constants.dog_class_element.junior_showmanship,
    level,
    placements: (input.placementsByLevel[level] || []).filter(Boolean),
  }));
  return {
    class_results: classResults,
    best_junior: cleanAward(input.bestJunior),
  };
}
